"use client";

import Link from "next/link";
import { useParams } from "next/navigation";
import { useEffect } from "react";
import Calendar from "lucide-react/dist/esm/icons/calendar";
import Globe from "lucide-react/dist/esm/icons/globe";
import MapPin from "lucide-react/dist/esm/icons/map-pin";
import ShoppingBag from "lucide-react/dist/esm/icons/shopping-bag";
import Star from "lucide-react/dist/esm/icons/star";
import MessageSquare from "lucide-react/dist/esm/icons/message-square";
import { PublicPageShell } from "@/components/layout/PublicPageShell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { SocialLinks } from "@/components/ui/social-links";
import {
  BuyerTrustBadge,
  calculateTrustLevel,
  getTrustLevelDescription,
} from "@/components/ui/buyer-trust-badge";
import { UserBadges } from "@/components/ui/user-badges";
import { SubscriptionBadge } from "@/components/ui/subscription-badge";
import { UserContextActions } from "@/components/context-actions/UserContextActions";
import { useProfile } from "@/contexts/ProfileContext";
import {
  useUserProfile,
  useUserProfileStats,
  useUserReviews,
} from "@/hooks/useUserProfile";
import { useUserBadges } from "@/hooks/useUserBadges";
import { usePublicTier } from "@/hooks/usePublicTier";
import { getCountryByCode } from "@/lib/countries";
import { format } from "date-fns";
import { logEvent } from "@/lib/events";

function ProfileSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: profile card */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <Skeleton className="h-24 w-24 rounded-full mx-auto" />
            <Skeleton className="h-6 w-40 mx-auto" />
            <Skeleton className="h-4 w-28 mx-auto" />
            <div className="space-y-2 pt-2 border-t border-border">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
        {/* Right: stats + activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 rounded-xl" style={{ animationDelay: `${i * 60}ms` }} />
            ))}
          </div>
          {/* Activity/reviews */}
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-border/30 last:border-0">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UserProfile() {
  const { username } = useParams<{ username: string }>();
  const { profile: viewerProfile } = useProfile();
  const { data: profile, error } = useUserProfile(username || "");
  const { data: stats } = useUserProfileStats(
    profile?.id || "",
    profile?.created_at,
    profile?.show_purchase_count ?? true
  );
  const { data: reviews } = useUserReviews(
    profile?.id || "",
    profile?.show_reviews_publicly ?? true
  );
  const { data: roles } = useUserBadges(profile?.user_id);
  const { data: userTier } = usePublicTier(profile?.id);

  // Track profile view once data loads
  useEffect(() => {
    if (!profile) return;
    logEvent(
      "user_profile_viewed",
      { profile_username: profile.username ?? username },
      { severity: "low" },
    );
  }, [profile?.id]);

  // "No data and no error" is still loading. isLoading is false while the
  // query is paused for the persisted-cache restore, which rendered "User not
  // found" on the client against the server's skeleton (hydration mismatch).
  if (!profile && !error) {
    return (
      <PublicPageShell mainAriaLabel="User profile" bleed>
        <ProfileSkeleton />
      </PublicPageShell>
    );
  }

  if (error || !profile) {
    return (
      <PublicPageShell mainAriaLabel="User profile" bleed className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 grid-pattern opacity-20" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[60px] will-change-transform" />
        </div>
        <div className="relative flex items-center justify-center min-h-[60dvh] px-4">
          <div className="text-center max-w-lg w-full">
            <h1 className="font-display text-2xl md:text-3xl font-bold mb-3">User not found</h1>
            <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
              This user doesn&apos;t exist or may have deleted their account.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <Link href="/">Go Home</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link href="/browse">Browse Assets</Link>
              </Button>
            </div>
          </div>
        </div>
      </PublicPageShell>
    );
  }

  const displayName = profile.display_name || profile.username || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const country = profile.show_country_on_profile && profile.country_code
    ? getCountryByCode(profile.country_code)
    : null;

  const memberSinceYear = new Date(profile.created_at).getFullYear();

  const trustLevel = stats
    ? calculateTrustLevel({
        purchaseCount: stats.purchaseCount,
        reviewCount: stats.reviewCount,
        accountAgeMonths: stats.accountAgeMonths,
        disputeCount: 0, // We don't expose dispute count to frontend
      })
    : "new";

  return (
    <PublicPageShell mainAriaLabel="User profile" width="wide">
      <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar - User Info */}
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <Avatar className="h-24 w-24 mb-4">
                    <AvatarImage src={profile.avatar_url || undefined} alt={displayName} />
                    <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
                  </Avatar>

                  <div className="space-y-2">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <h1 className="font-display text-xl font-bold">{displayName}</h1>
                      {userTier && userTier !== "free" && (
                        <SubscriptionBadge tier={userTier} size="sm" />
                      )}
                      <UserBadges
                        user={{
                          is_seller: profile.is_seller,
                          seller_verified: profile.seller_verified,
                          is_early_founder: profile.is_early_founder,
                          roles: roles || [],
                        }}
                        size="sm"
                      />
                    </div>

                    {profile.username && (
                      <p className="text-muted-foreground">@{profile.username}</p>
                    )}

                    <BuyerTrustBadge level={trustLevel} />
                    <p className="text-xs text-muted-foreground mt-1">
                      {getTrustLevelDescription(trustLevel)}
                    </p>
                  </div>

                  {profile.bio && (
                    <p className="text-muted-foreground mt-4 text-sm">
                      {profile.bio}
                    </p>
                  )}
                </div>

                {/* Owner gets their edit shortcuts, admins get the full
                    moderation surface; a plain visitor sees nothing. */}
                <UserContextActions
                  profileId={profile.id}
                  isOwnProfile={viewerProfile?.id === profile.id}
                  className="mt-4 w-full"
                />

                <Separator className="my-6" />

                {/* Stats */}
                {stats && (
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {profile.show_purchase_count && (
                      <div>
                        <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                          <ShoppingBag className="h-4 w-4" />
                        </div>
                        <p className="text-lg font-bold">{stats.purchaseCount}</p>
                        <p className="text-xs text-muted-foreground">Purchases</p>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                        <MessageSquare className="h-4 w-4" />
                      </div>
                      <p className="text-lg font-bold">{stats.reviewCount}</p>
                      <p className="text-xs text-muted-foreground">Reviews</p>
                    </div>
                    {stats.averageRatingGiven !== null && (
                      <div>
                        <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                          <Star className="h-4 w-4" />
                        </div>
                        <p className="text-lg font-bold">
                          {stats.averageRatingGiven.toFixed(1)}
                        </p>
                        <p className="text-xs text-muted-foreground">Avg Rating</p>
                      </div>
                    )}
                  </div>
                )}

                <Separator className="my-6" />

                {/* Meta Info */}
                <div className="space-y-3 text-sm">
                  {country && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {country.flag} {country.name}
                      </span>
                    </div>
                  )}

                  {profile.website && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-4 w-4" />
                      <a
                        href={profile.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-foreground transition-colors truncate"
                      >
                        {profile.website.replace(/^https?:\/\//, "")}
                      </a>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>Member since {memberSinceYear}</span>
                  </div>

                  <SocialLinks
                    links={{
                      social_youtube: profile.social_youtube,
                      social_twitter: profile.social_twitter,
                      social_discord: profile.social_discord,
                      social_tiktok: profile.social_tiktok,
                      social_instagram: profile.social_instagram,
                    }}
                    className="pt-2"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Reviews */}
          <div className="lg:col-span-2 space-y-6">
            {profile.show_reviews_publicly && reviews && reviews.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Reviews</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {reviews.map((review, index) => (
                    <div key={review.id}>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Link href={`/asset/${review.asset.slug}`}
                            className="font-medium hover:text-primary transition-colors"
                          >
                            {review.asset.title}
                          </Link>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.rating
                                    ? "fill-warning text-warning"
                                    : "text-muted-foreground"
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        {review.title && (
                          <h4 className="font-medium">{review.title}</h4>
                        )}
                        {review.content && (
                          <p className="text-muted-foreground text-sm">
                            {review.content}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(review.created_at), "MMM d, yyyy")}
                        </p>
                      </div>
                      {index < reviews.length - 1 && (
                        <Separator className="mt-6" />
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-display text-lg font-medium mb-2">No Public Reviews</h3>
                  <p className="text-muted-foreground">
                    {profile.show_reviews_publicly
                      ? "This user hasn't written any reviews yet."
                      : "This user has chosen to keep their reviews private."}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
    </PublicPageShell>
  );
}
